/**
 * PlayerPageHeaderSkeletonView - Loading skeleton for PlayerPageHeader
 *
 * Displays placeholder animations while player data is loading.
 * Maintains layout stability to prevent content jump when data loads.
 * @remarks This is a View component - contains no business logic, hooks, or state
 */

import { memo } from "react";

import { Box } from "@/components/atoms/Box";
import { Stack } from "@/components/atoms/Stack";
import { Skeleton } from "@/components/atoms/Skeleton";

import { TEST_ID_PLAYER_DETAIL } from "e2e/testId";

import type { PlayerPageHeaderSkeletonViewProps } from "@/features/player-detail/utils/playerPageHeaderTypes";

/** Index array for indicator rail skeleton mapping */
const INDICATOR_INDICES = [1, 2, 3, 4, 5] as const;

/**
 * PlayerPageHeaderSkeletonView renders loading placeholders for the header
 * @param props - View props including theme state
 * @param props.isDark - Whether dark mode is enabled for skeleton styling
 * @returns VStack containing skeleton elements matching header layout
 */
const PlayerPageHeaderSkeletonView = ({
  isDark,
}: PlayerPageHeaderSkeletonViewProps) => (
  <Stack
    {...{ "data-testid": TEST_ID_PLAYER_DETAIL.HEADER_SKELETON }}
    className="w-full gap-4 p-4"
  >
    {/* Identity Block Skeleton */}
    <Stack className="w-full gap-2">
      {/* Player Name Skeleton */}
      <Skeleton
        className={`h-8 w-48 rounded-none ${isDark ? "bg-surface-subtle" : "bg-surface-muted"}`}
        isLoaded={false}
      />

      {/* Badges Row Skeleton */}
      <Stack direction="horizontal" className="gap-2">
        <Skeleton
          className={`h-6 w-32 rounded-none ${isDark ? "bg-surface-subtle" : "bg-surface-muted"}`}
          isLoaded={false}
        />
        <Skeleton
          className={`h-6 w-40 rounded-none ${isDark ? "bg-surface-subtle" : "bg-surface-muted"}`}
          isLoaded={false}
        />
        <Skeleton
          className={`h-6 w-20 rounded-none ${isDark ? "bg-surface-subtle" : "bg-surface-muted"}`}
          isLoaded={false}
        />
      </Stack>

      {/* Tidbits Row Skeleton */}
      <Stack direction="horizontal" className="gap-2">
        <Skeleton
          className={`h-4 w-24 rounded-none ${isDark ? "bg-surface-subtle" : "bg-surface-muted"}`}
          isLoaded={false}
        />
        <Skeleton
          className={`h-4 w-20 rounded-none ${isDark ? "bg-surface-subtle" : "bg-surface-muted"}`}
          isLoaded={false}
        />
        <Skeleton
          className={`h-4 w-32 rounded-none ${isDark ? "bg-surface-subtle" : "bg-surface-muted"}`}
          isLoaded={false}
        />
        <Skeleton
          className={`h-4 w-16 rounded-none ${isDark ? "bg-surface-subtle" : "bg-surface-muted"}`}
          isLoaded={false}
        />
        <Skeleton
          className={`h-4 w-24 rounded-none ${isDark ? "bg-surface-subtle" : "bg-surface-muted"}`}
          isLoaded={false}
        />
      </Stack>
    </Stack>

    {/* Indicator Rail Skeleton */}
    <Stack direction="horizontal" className="w-full gap-3">
      {INDICATOR_INDICES.map(index => (
        <Box
          key={index}
          className={`min-w-28 flex-1 rounded-sm border border-outline-default px-4 py-3 ${isDark ? "bg-surface-raised" : "bg-surface-base"}`}
        >
          <Skeleton
            className={`mx-auto h-8 w-12 rounded-none ${isDark ? "bg-surface-subtle" : "bg-surface-muted"}`}
            isLoaded={false}
          />
          <Skeleton
            className={`mx-auto mt-1 h-3 w-16 rounded-none ${isDark ? "bg-surface-subtle" : "bg-surface-muted"}`}
            isLoaded={false}
          />
        </Box>
      ))}
    </Stack>
  </Stack>
);

PlayerPageHeaderSkeletonView.displayName = "PlayerPageHeaderSkeletonView";

export default memo(PlayerPageHeaderSkeletonView);
